import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import travelTypeObject from '@/entities/travelType/interface'

import {
  ALL_TENANT_SETTINGS_QUERY,
  SINGLE_TENANT_SETTINGS_QUERY,
  TENANT_SETTINGS_DEPENDENTS_QUERY,
  CREATE_TENANT_SETTINGS_MUTATION,
  UPDATE_TENANT_SETTINGS_MUTATION,
  DELETE_TENANT_SETTINGS_MUTATION,
  FORCE_DELETE_TENANT_SETTINGS_MUTATION,
  ADD_TENANT_SETTINGS_TRAVEL_TYPES_MUTATION,
  REMOVE_TENANT_SETTINGS_TRAVEL_TYPES_MUTATION,
  CREATE_TENANT_SETTINGS_TRAVEL_TYPES_MUTATION,
  DELETE_TENANT_SETTINGS_TRAVEL_TYPES_MUTATION,
  UPDATE_TENANT_SETTINGS_TRAVEL_TYPES_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const travelObjectTenantSettingsRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_TENANT_SETTINGS_QUERY, variables).then((data: any) => {
      return hydrateObject(data.tenantSettings)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_TENANT_SETTINGS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.tenantSettings)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_TENANT_SETTINGS_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.tenantSettings),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_TENANT_SETTINGS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.tenantSettings),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(TENANT_SETTINGS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([TENANT_SETTINGS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_TENANT_SETTINGS_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.tenantSettings)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_TENANT_SETTINGS_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.tenantSettings)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_TENANT_SETTINGS_MUTATION, variables).then((data: any) => {
      return data.deleteTravelObjectTenantSettings
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_TENANT_SETTINGS_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTravelObjectTenantSettings
    })
  },

  assignTravelTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TENANT_SETTINGS_TRAVEL_TYPES_MUTATION, variables)
  },

  unassignTravelTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TENANT_SETTINGS_TRAVEL_TYPES_MUTATION, variables)
  },

  createTravelTypes: async (id: string, items: Array<travelTypeObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypes: items,
      },
    }
    return graphQLClient.request(CREATE_TENANT_SETTINGS_TRAVEL_TYPES_MUTATION, variables)
  },

  deleteTravelTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TENANT_SETTINGS_TRAVEL_TYPES_MUTATION, variables)
  },

  updateTravelTypesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TENANT_SETTINGS_TRAVEL_TYPES_ORDER_MUTATION, variables)
  },
}

export default travelObjectTenantSettingsRepository
